'use client'

import type { Article, GuidePagination } from './types'

import { useEffect, useMemo, useRef, useState } from 'react'
import { debounce, isEmpty } from 'lodash'

import { fetchArticles } from './api'
import { DEFAULT_PAGINATION, FEATURED_COUNT } from './types'

type UseArticleFiltersOptions = {
  /** Restricts the listing to one guide category. Omitted on /personal/guides. */
  categorySlug?: string
  initial?: {
    articles?: Article[] | null
    featured?: Article[] | null
    pagination?: GuidePagination | null
  }
  /**
   * Skip the very first fetch because the server already seeded page 1.
   * `Guides.tsx` does this; `GuideCategory.tsx` historically did not, so it
   * defaults to false to preserve each page's existing behaviour.
   */
  skipInitialFetch?: boolean
}

/**
 * Owns the guides listing's filter state: search, sort, topic tags and
 * pagination, plus the debounced + abortable fetch that keeps the list in sync.
 *
 * Extracted verbatim from `Guides.tsx` — the debounce/cleanup semantics are
 * load-bearing. `debouncedFetch` is recreated on [search, sort, topics] and the
 * effect cleanup calls BOTH `controller.abort()` and `debouncedFetch.cancel()`;
 * dropping the cancel lets a stale response overwrite a fresh one.
 */
/** Order-insensitive fingerprint of the filter state that produced a result set. */
export function signatureOf(page: number, search: string, sort: string, topics: string[]): string {
  return [page, search, sort, [...topics].sort().join(',')].join('|')
}

export function useArticleFilters({
  categorySlug,
  initial,
  skipInitialFetch = false
}: UseArticleFiltersOptions) {
  const [articleList, setArticleList] = useState<Article[]>(initial?.articles ?? [])
  const [guidesList, setGuidesList] = useState<Article[]>(
    initial?.featured ?? initial?.articles?.slice(0, FEATURED_COUNT) ?? []
  )
  const [pagination, setPagination] = useState<GuidePagination>(
    initial?.pagination ?? DEFAULT_PAGINATION
  )
  const [search, setSearch] = useState('')
  const [selectedSort, setSelectedSort] = useState('')
  const [selectedTopics, setSelectedTopics] = useState<string[]>([])
  const [isData, setIsData] = useState(true)

  /**
   * Signature of the filter state the SERVER already rendered, or null when the
   * page was not seeded (then every mount must fetch).
   *
   * This replaces a one-shot `useRef(skipInitialFetch)` that was cleared the
   * first time the fetch effect ran. That guard was fragile: the effect's deps
   * include `selectedTopics` (an array), so any identity change re-ran it once
   * more with the flag already cleared and fired the exact query the server had
   * already answered — observed intermittently on /personal/guides/ and
   * /personal/guides/[category]/ and caught by tests/no-client-cms-fetch.spec.ts.
   *
   * Comparing state to a signature is idempotent: it cannot be defeated by an
   * extra render, a changed reference, or a double-invoked effect.
   */
  const seededSignature = useRef<string | null>(
    skipInitialFetch ? signatureOf(initial?.pagination?.page ?? 1, '', '', []) : null
  )

  const fetchArticleData = async (
    sortValue = 'desc',
    searchInput?: string,
    filterData?: string[],
    page = 1,
    controller?: AbortController
  ) => {
    const result = await fetchArticles({
      sort: sortValue,
      search: searchInput ?? '',
      tagSlugs: filterData ?? [],
      page,
      categorySlug,
      controller
    })
    if (!result) return

    // The unfiltered, unsorted, unsearched view also drives the featured strip.
    if (isEmpty(filterData) && sortValue === '' && !searchInput) {
      setGuidesList(result.articles?.slice(0, FEATURED_COUNT) ?? [])
      setIsData((result.articles?.length ?? 0) > 0)
    }
    setArticleList(result.articles)
    if (result.pagination) setPagination(result.pagination)
  }

  // useMemo, not useCallback: `debounce(...)` is a CALL, so under useCallback it
  // re-ran on every render and built a throwaway debounced function that was then
  // discarded — and React Compiler bails out of optimizing the whole hook rather
  // than memoize a non-inline argument. useMemo builds it only when the filters
  // actually change, which is the identity rule this hook already relied on.
  const debouncedFetch = useMemo(
    () =>
      debounce(({ selectedSort, search, selectedTopics, pagination, controller }) => {
        fetchArticleData(selectedSort, search, selectedTopics, pagination?.page, controller)
      }, 500),
    [search, selectedSort, selectedTopics]
  )

  useEffect(() => {
    // On initial mount the page is already 1; skip to avoid a spurious re-render
    // that would re-trigger the fetch effect below.
    if (seededSignature.current !== null) return
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [search, selectedSort, selectedTopics])

  useEffect(() => {
    // While the filter state still matches what the server rendered, the list on
    // screen already IS that response — fetching it again is a wasted round-trip
    // and a visible content swap. On the first divergence the shortcut is retired
    // for good, so clearing filters later still refetches rather than showing a
    // stale filtered list.
    if (seededSignature.current !== null) {
      if (
        signatureOf(pagination?.page ?? 1, search, selectedSort, selectedTopics) ===
        seededSignature.current
      ) {
        return
      }
      seededSignature.current = null
    }

    const controller = new AbortController()

    debouncedFetch({ search, selectedSort, selectedTopics, pagination, controller })

    return () => {
      controller.abort()
      debouncedFetch.cancel()
    }
  }, [pagination?.page, search, selectedSort, selectedTopics])

  const setPage = (page: number) => setPagination(prev => ({ ...prev, page }))

  return {
    articleList,
    guidesList,
    pagination,
    setPage,
    /** Raw setter — `PaginationComponent` passes an updater function. */
    setPagination,
    search,
    setSearch,
    selectedSort,
    setSelectedSort,
    selectedTopics,
    setSelectedTopics,
    isData
  }
}
